import { useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { useAuthUser } from "../../contexts/AuthUserContext";
import { useLocalization } from "../../contexts/LocalizationContext";
import { GET_NOTIFICATIONS } from "../../graphql/queries";
import {
  MARK_ALL_NOTIFICATIONS_READ,
  MARK_NOTIFICATION_READ,
} from "../../graphql/mutations";
import pageStyles from "../../styles/PageLayout.module.css";
import styles from "./NotificationsPage.module.css";

const NotificationsPage = () => {
  const { user } = useAuthUser();
  const navigate = useNavigate();
  const { t } = useLocalization();
  const authToken = user?.token;
  const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};

  const { data, loading, refetch } = useQuery(GET_NOTIFICATIONS, {
    variables: { limit: 100 },
    skip: !authToken,
    context: { headers },
    fetchPolicy: "network-only",
  });

  const [markNotificationRead] = useMutation(MARK_NOTIFICATION_READ);
  const [markAllNotificationsRead, { loading: markingAll }] = useMutation(
    MARK_ALL_NOTIFICATIONS_READ
  );

  const notifications = useMemo(
    () => data?.myNotifications?.items ?? [],
    [data]
  );
  const unreadCount = data?.myNotifications?.unreadCount ?? 0;

  const handleMarkAll = useCallback(async () => {
    if (!authToken || !unreadCount) return;
    try {
      await markAllNotificationsRead({ context: { headers } });
      await refetch?.();
    } catch (err) {
      console.error("markAllNotificationsRead.error", err);
    }
  }, [authToken, unreadCount, markAllNotificationsRead, headers, refetch]);

  const handlePressNotification = useCallback(
    async (notification) => {
      if (!authToken) return;
      try {
        if (!notification.read) {
          await markNotificationRead({
            variables: { id: notification.id },
            context: { headers },
          });
          await refetch?.();
        }
      } catch (err) {
        console.error("markNotificationRead.error", err);
      }

      if (notification.type === "REPORT") {
        navigate("/reports");
        return;
      }
      if (notification.type === "FOUNDATION_STAGE") {
        navigate("/");
      }
    },
    [authToken, headers, markNotificationRead, navigate, refetch]
  );

  if (!user) {
    return (
      <section className={`${pageStyles.page} ${styles.wrapper}`} data-page="notifications">
        <div className={pageStyles.emptyState}>
        <h2>
          {t("NotificationScreen.authRequired", {
            defaultValue: "Sign in to view notifications.",
          })}
        </h2>
        <p>
          Visit <Link to="/login">Login</Link> and drop your token to sync
          notifications.
        </p>
        </div>
      </section>
    );
  }

  return (
    <section className={`${pageStyles.page} ${styles.wrapper}`} data-page="notifications">
      <header className={pageStyles.sectionHeader}>
        <h2>
          {t("NotificationScreen.title", { defaultValue: "Notifications" })}
        </h2>
        {/* <button
          className={pageStyles.sectionHeaderButton}
          onClick={() => refetch?.()}
        >
          Refresh ({unreadCount} unread)
        </button> */}
        {unreadCount > 0 && (
          <button
            className={pageStyles.sectionHeaderButton}
            onClick={handleMarkAll}
            disabled={markingAll}
          >
            {t("NotificationScreen.markAll", {
              defaultValue: "Mark all as read",
            })}
          </button>
        )}
      </header>
      {loading && (
        <p>
          {t("NotificationScreen.loading", { defaultValue: "Loading notifications…" })}
        </p>
      )}
      {!loading && notifications.length === 0 && (
        <p className={pageStyles.empty}>
          {t("NotificationScreen.empty", { defaultValue: "No notifications yet." })}
        </p>
      )}
      <ul className={`${pageStyles.notificationList} ${styles.list}`}>
        {notifications.map((notification) => {
          const timestamp = notification.createdAt || notification.updatedAt;
          return (
            <li
              key={notification.id}
              className={`${pageStyles.notificationListItem} ${
                notification.read ? "" : pageStyles.notificationListItemUnread
              }`}
              onClick={() => handlePressNotification(notification)}
            >
              <div>
                <h3>{notification.title}</h3>
                <p>{notification.body}</p>
              </div>
              <small>
                {timestamp ? new Date(timestamp).toLocaleString() : "—"}
              </small>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default NotificationsPage;
